import React, { useMemo, useState } from 'react';
import { ExternalLink, FileText, Mic, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getBackendBaseUrl } from '../../utils/apiBase';
import { formatDisplayDate, formatPersonDateRange } from '../../utils/dateDisplay';
import { getPersonDisplayName } from '../../utils/personArchiveInsight';
import {
  buildLegacyBookReadiness,
  formatRelationshipName,
} from '../../utils/legacyBookPreview';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  getMemorialPath,
  getMemorialPublicUrl,
  isMemorialPublished,
} from '../../utils/memorial';
import {
  getStoryExcerpt,
  getStoryPrimaryTitle,
  getStoryPromptLine,
  storyHasVoice,
} from '../../utils/storyDisplay';
import { personHref } from '../../utils/prettyArchiveLinks';
import { cn } from '@/lib/utils';

const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif']);

function isImageDocument(doc) {
  const ext = (doc?.file_type || '').toLowerCase();
  return IMAGE_EXT.has(ext);
}

function ProfilePhoto({ url, name, className }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className={className}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  return (
    <div
      className={cn('flex items-center justify-center bg-[#f5efe6] text-[#8b7355]', className)}
      aria-hidden
    >
      <span className="font-serif text-2xl">{initials || <User className="h-8 w-8" />}</span>
    </div>
  );
}

function DocPhoto({ doc, backendBase }) {
  const [broken, setBroken] = useState(false);
  const src = resolveAssetUrl(backendBase, doc.file_url);
  if (!src || broken) {
    return <div className="legacy-book-placeholder">{doc.title || 'Photo'}</div>;
  }
  return (
    <img
      src={src}
      alt={doc.title || 'Photo'}
      loading="lazy"
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  );
}

function ReadinessPanel({ readiness, personHref: backHref }) {
  if (!readiness || readiness.complete === readiness.total) return null;
  return (
    <div
      className="legacy-book-no-print mb-6 rounded-2xl border border-[#c9a961]/30 bg-[#fffaf3]/90 p-4 sm:p-5"
      data-testid="legacy-book-readiness"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
        Prepare for a richer Legacy Book
      </p>
      <p className="mt-1 text-sm text-[#5c534a]">
        You can preview anytime. These gentle prompts help the book feel complete when you are ready.
      </p>
      <ul className="mt-3 space-y-2">
        {readiness.items.map((item) => (
          <li key={item.id} className="flex items-start gap-2 text-sm">
            <span
              className={cn(
                'mt-1 h-2 w-2 shrink-0 rounded-full',
                item.done ? 'bg-[#3d5c4a]' : 'bg-[#c9a961]/60',
              )}
              aria-hidden
            />
            <span className={item.done ? 'text-[#6c6258] line-through decoration-[#c9a961]/40' : 'text-[#3d3833]'}>
              {item.label}
            </span>
          </li>
        ))}
      </ul>
      {backHref ? (
        <p className="mt-3 text-xs text-[#8b7355]">
          <Link to={backHref} className="underline underline-offset-2 hover:text-[#5c4a32]">
            Return to profile to add more
          </Link>
        </p>
      ) : null}
    </div>
  );
}

export function LegacyBookPreviewContent({
  person,
  tree,
  relationships,
  stories = [],
  documents = [],
  memorial,
  options,
}) {
  const backendBase = getBackendBaseUrl();
  const name = getPersonDisplayName(person);
  const years = formatPersonDateRange(person);
  const profilePhotoUrl = resolveAssetUrl(backendBase, person?.profile_image_url);
  const backHref = person ? personHref(person) : '/dashboard';

  const voiceStories = useMemo(
    () => stories.filter((s) => storyHasVoice(s) || s.transcript_source === 'voice'),
    [stories],
  );

  const voiceCount = voiceStories.length;
  const readiness = useMemo(
    () => buildLegacyBookReadiness({ person, stories, documents, voiceCount, memorial }),
    [person, stories, documents, voiceCount, memorial],
  );

  const showPhotoCover = options.coverStyle === 'photo' && profilePhotoUrl;
  const memorialPublished = isMemorialPublished(memorial);
  const memorialPath = memorial?.slug ? getMemorialPath(memorial.slug) : null;
  const memorialPublicUrl = memorialPublished
    ? getMemorialPublicUrl(memorial?.public_url, memorial?.slug)
    : null;

  const timelineEntries = useMemo(() => {
    const entries = [];
    if (person?.birth_date) {
      entries.push({ label: 'Born', date: formatDisplayDate(person.birth_date) });
    }
    if (person?.death_date) {
      entries.push({ label: 'Died', date: formatDisplayDate(person.death_date) });
    }
    for (const p of relationships?.partnerships || []) {
      if (p?.partnership_start_date) {
        entries.push({
          label: p.partnership_type === 'marriage' ? 'Marriage' : 'Partnership',
          date: formatDisplayDate(p.partnership_start_date),
          detail: formatRelationshipName(p),
        });
      }
    }
    return entries;
  }, [person, relationships]);

  const photoDocs = documents.filter(isImageDocument).slice(0, 9);
  const otherDocs = documents.filter((d) => !isImageDocument(d)).slice(0, 6);

  const featuredExcerpt =
    (person?.featured_memory_excerpt || '').trim() ||
    (stories[0] ? getStoryExcerpt(stories[0], 280) : '');

  return (
    <div data-testid="legacy-book-preview-content">
      <ReadinessPanel readiness={readiness} personHref={backHref} />

      {/* Cover */}
      <article
        className={cn(
          'legacy-book-page legacy-book-page--cover',
          showPhotoCover && 'legacy-book-page--cover-photo',
        )}
        style={showPhotoCover ? { backgroundImage: `url(${profilePhotoUrl})` } : undefined}
        data-testid="legacy-book-cover"
      >
        <div className="legacy-book-cover-inner">
          {!showPhotoCover && profilePhotoUrl ? (
            <div className="mx-auto mb-6 h-28 w-28 overflow-hidden rounded-full border-2 border-[#c9a961]/40 shadow-md">
              <ProfilePhoto url={profilePhotoUrl} name={name} className="h-full w-full object-cover" />
            </div>
          ) : null}
          <h1 className="legacy-book-name">{name}</h1>
          {years ? <p className="legacy-book-years">{years}</p> : null}
          <p className="legacy-book-subtitle">A life remembered</p>
          {tree?.name ? <p className="legacy-book-archive-name">{tree.name}</p> : null}
        </div>
      </article>

      {/* Life overview */}
      {options.sections.overview ? (
        <article className="legacy-book-page" data-testid="legacy-book-overview">
          <p className="legacy-book-section-label">Life overview</p>
          <h2 className="legacy-book-section-title">{name}</h2>
          {(person?.biographical_notes || '').trim() ? (
            <p className="legacy-book-body whitespace-pre-wrap">{person.biographical_notes.trim()}</p>
          ) : (
            <p className="legacy-book-body italic">A life summary can be added on the profile to open this chapter.</p>
          )}

          {Array.isArray(person?.life_themes) && person.life_themes.filter(Boolean).length > 0 ? (
            <div className="mt-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3d5c4a]">Known for</p>
              <div className="mt-2">
                {person.life_themes.filter(Boolean).map((theme) => (
                  <span key={theme} className="legacy-book-theme-chip">
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {featuredExcerpt ? (
            <blockquote className="mt-8 border-l-2 border-[#c9a961]/50 pl-4 font-serif text-lg italic leading-relaxed text-[#4a433c]">
              {featuredExcerpt}
            </blockquote>
          ) : null}

          {Array.isArray(person?.loved_items) && person.loved_items.length > 0 ? (
            <div className="mt-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3d5c4a]">What they loved</p>
              <ul className="mt-2 space-y-1 text-sm text-[#5c534a]">
                {person.loved_items.map((item, idx) => (
                  <li key={`${item}-${idx}`}>· {typeof item === 'string' ? item : item?.label || item?.name}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </article>
      ) : null}

      {/* Timeline */}
      {options.sections.overview && timelineEntries.length > 0 ? (
        <article className="legacy-book-page" data-testid="legacy-book-timeline">
          <p className="legacy-book-section-label">Key dates</p>
          <h2 className="legacy-book-section-title">Timeline</h2>
          <ul className="space-y-3">
            {timelineEntries.map((entry, idx) => (
              <li key={`${entry.label}-${idx}`} className="flex gap-4 border-b border-[#e8e2d8]/60 pb-3 last:border-0">
                <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-[0.1em] text-[#3d5c4a]">
                  {entry.label}
                </span>
                <div>
                  <p className="text-sm text-[#2c2824]">{entry.date}</p>
                  {entry.detail ? <p className="text-xs text-[#8b7355]">{entry.detail}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      {/* Stories */}
      {options.sections.stories ? (
        <article className="legacy-book-page" data-testid="legacy-book-stories">
          <p className="legacy-book-section-label">Stories</p>
          <h2 className="legacy-book-section-title">Memories preserved</h2>
          {stories.length === 0 ? (
            <p className="legacy-book-body italic">Stories added in Story Studio will appear here.</p>
          ) : (
            stories.map((story) => {
              const title = getStoryPrimaryTitle(story);
              const prompt = getStoryPromptLine(story);
              const body = (story.polished_transcript || story.raw_transcript || '').trim();
              const excerpt = body || getStoryExcerpt(story, 400);
              return (
                <div key={story.id} className="legacy-book-story">
                  <h3 className="legacy-book-story-title">{title}</h3>
                  {prompt ? <p className="legacy-book-story-meta">{prompt}</p> : null}
                  {story.created_at ? (
                    <p className="legacy-book-story-meta">{formatDisplayDate(story.created_at?.slice?.(0, 10) || story.created_at)}</p>
                  ) : null}
                  {excerpt ? (
                    <p className="legacy-book-body whitespace-pre-wrap">{excerpt}</p>
                  ) : (
                    <p className="legacy-book-body italic">Voice or draft memory — open in the archive to continue.</p>
                  )}
                </div>
              );
            })
          )}
        </article>
      ) : null}

      {/* Voice */}
      {options.sections.voice ? (
        <article className="legacy-book-page" data-testid="legacy-book-voice">
          <p className="legacy-book-section-label">Voice memories</p>
          <h2 className="legacy-book-section-title">Voices in the archive</h2>
          {voiceStories.length === 0 ? (
            <p className="legacy-book-body">Voice memories saved in the archive will be listed here for the printed book.</p>
          ) : (
            <>
              <p className="legacy-book-body mb-4">Voice memories saved in the archive — audio plays in your private family space.</p>
              <ul>
                {voiceStories.map((story) => (
                  <li key={story.id} className="legacy-book-voice-item flex gap-3">
                    <Mic className="mt-0.5 h-4 w-4 shrink-0 text-[#8b7355]" aria-hidden />
                    <div>
                      <p className="text-sm font-medium text-[#2c2824]">{getStoryPrimaryTitle(story)}</p>
                      <p className="text-xs text-[#8b7355]">Preserved in archive</p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </article>
      ) : null}

      {/* Photos & documents */}
      {options.sections.photos ? (
        <article className="legacy-book-page" data-testid="legacy-book-photos">
          <p className="legacy-book-section-label">Photos &amp; documents</p>
          <h2 className="legacy-book-section-title">Visual keepsakes</h2>
          {documents.length === 0 ? (
            <p className="legacy-book-body italic">Photos and documents linked to this profile will appear here.</p>
          ) : (
            <>
              {photoDocs.length > 0 ? (
                <div className="legacy-book-photo-grid">
                  {photoDocs.map((doc) => (
                      <div key={doc.id} className="legacy-book-photo-tile">
                        <DocPhoto doc={doc} backendBase={backendBase} />
                      </div>
                    ))}
                </div>
              ) : null}
              {otherDocs.length > 0 ? (
                <ul className="mt-6 space-y-2">
                  {otherDocs.map((doc) => (
                    <li key={doc.id} className="flex items-start gap-2 text-sm text-[#5c534a]">
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#8b7355]" aria-hidden />
                      <span>{doc.title || 'Document'}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </article>
      ) : null}

      {/* Family */}
      {options.sections.family ? (
        <article className="legacy-book-page" data-testid="legacy-book-family">
          <p className="legacy-book-section-label">Family</p>
          <h2 className="legacy-book-section-title">Family connections</h2>
          <div className="legacy-book-family-list">
            {[
              { key: 'parents', label: 'Parents', items: relationships?.parents },
              { key: 'partnerships', label: 'Spouse / partner', items: relationships?.partnerships },
              { key: 'children', label: 'Children', items: relationships?.children },
              { key: 'siblings', label: 'Siblings', items: relationships?.siblings },
            ].map(({ key, label, items }) =>
              items?.length ? (
                <div key={key} className="legacy-book-family-group">
                  <h4>{label}</h4>
                  <ul>
                    {items.map((p) => (
                      <li key={p.id}>{formatRelationshipName(p)}</li>
                    ))}
                  </ul>
                </div>
              ) : null,
            )}
          </div>
          {!relationships?.parents?.length &&
          !relationships?.partnerships?.length &&
          !relationships?.children?.length &&
          !relationships?.siblings?.length ? (
            <p className="legacy-book-body italic">Family connections can be linked on the profile.</p>
          ) : null}
        </article>
      ) : null}

      {/* Memorial */}
      {options.sections.memorial ? (
        <article className="legacy-book-page" data-testid="legacy-book-memorial">
          <p className="legacy-book-section-label">Memorial</p>
          <h2 className="legacy-book-section-title">Remembered with love</h2>
          {memorialPublished && memorialPath ? (
            <>
              <p className="legacy-book-body">Memorial page connected — a public tribute when your family chooses to share.</p>
              <a
                href={memorialPublicUrl || memorialPath}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-[#3d5c4a] underline underline-offset-2"
              >
                View memorial page
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </>
          ) : memorial ? (
            <p className="legacy-book-body">Memorial workspace started — publish when your family is ready.</p>
          ) : (
            <p className="legacy-book-body italic">A memorial page can be prepared privately from this profile.</p>
          )}
        </article>
      ) : null}
    </div>
  );
}

export default LegacyBookPreviewContent;
